import React, { useState, useEffect } from 'react';
import IconPicker from './IconPicker';

const VerbExplorerAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [verbExplorerSettings, setVerbExplorerSettings] = useState({ is_visible: false, is_pro_only: false, beta_badge_visible: true, beta_badge_text: 'Beta', beta_badge_description: '', rate_limit_enabled: true, rate_limit_per_hour: 30 });
  const [verbExplorerSubTab, setVerbExplorerSubTab] = useState('settings');
  const [verbExplorerCategories, setVerbExplorerCategories] = useState([]);
  const [verbExplorerAllVerbs, setVerbExplorerAllVerbs] = useState([]);
  const [verbExplorerVerbAccess, setVerbExplorerVerbAccess] = useState([]);
  const [editingVECategory, setEditingVECategory] = useState(null);
  const [newVECategory] = useState({ name: '', icon: '', order: 50 });
  const [veAssigningCategory, setVeAssigningCategory] = useState(null);
  const [veSaving, setVeSaving] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [iconPickerTarget, setIconPickerTarget] = useState(null); // 'all', 'page', 'form', or category id

  const handleIconSelect = async (url) => {
    setIconPickerOpen(false);
    if (iconPickerTarget === 'form') {
      setEditingVECategory(p => ({ ...p, icon_url: url }));
    } else if (iconPickerTarget === 'all') {
      setVerbExplorerSettings(p => ({ ...p, all_icon_url: url }));
      showNotification('Icon selected — click "Save Settings" to apply', 'info');
    } else if (iconPickerTarget === 'page') {
      setVerbExplorerSettings(p => ({ ...p, page_icon_url: url }));
      showNotification('Icon selected — click "Save Settings" to apply', 'info');
    } else if (iconPickerTarget) {
      try {
        const res = await fetch(`${API}/admin/verb-explorer/categories/${iconPickerTarget}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ icon_url: url })
        });
        if (res.ok) {
          setVerbExplorerCategories(prev => prev.map(c => c.id === iconPickerTarget ? { ...c, icon_url: url } : c));
          showNotification('Category icon saved!', 'success');
        }
      } catch {}
    }
  };
  const [verbImageData, setVerbImageData] = useState([]); // verbs with image info grouped by category
  const [verbImageFilter, setVerbImageFilter] = useState('all'); // 'all', 'with-images', 'no-images'
  const [verbImageSearch, setVerbImageSearch] = useState('');
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState(null); // { verbName, categoryId, field: 'he_image' | 'she_image' }
  const [imagePickerFolders, setImagePickerFolders] = useState([]);
  const [imagePickerSelectedFolder, setImagePickerSelectedFolder] = useState(null);
  const [imagePickerAssets, setImagePickerAssets] = useState([]);
  const [imagePickerSearch, setImagePickerSearch] = useState('');
  const [imagePickerLoading, setImagePickerLoading] = useState(false);

  useEffect(() => {
    const authHeaders = { Authorization: `Bearer ${adminToken}` };
    Promise.all([
      fetch(`${API}/admin/verb-explorer/settings`, { headers: authHeaders }),
      fetch(`${API}/admin/verb-explorer/categories`, { headers: authHeaders }),
      fetch(`${API}/admin/verb-explorer/all-verbs`, { headers: authHeaders })
    ]).then(async ([veSetRes, veCatRes, veVerbsRes]) => {
      if (veSetRes.ok) {
        const vs = await veSetRes.json();
        if (!vs.page_title) vs.page_title = 'Darija Verb Explorer';
        if (!vs.page_subtitle) vs.page_subtitle = 'Browse & learn Moroccan verb conjugations';
        setVerbExplorerSettings(vs);
      }
      if (veCatRes.ok) { const vc = await veCatRes.json(); setVerbExplorerCategories(vc.categories || []); }
      if (veVerbsRes.ok) { const vv = await veVerbsRes.json(); setVerbExplorerAllVerbs(vv.verbs || []); }
    }).catch(e => console.error('Failed to load verb explorer admin data:', e));
  }, [API, adminToken]);

  return (
    <>
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'settings', label: 'Settings', icon: '⚙️' },
          { key: 'categories', label: 'Categories', icon: '📂' },
          { key: 'access', label: 'Verb Access', icon: '🔑' },
          { key: 'images', label: 'Verb Images', icon: '🖼️' }
        ].map(tab => (
          <button key={tab.key}
            onClick={() => {
              setVerbExplorerSubTab(tab.key);
              if (tab.key === 'categories' && verbExplorerCategories.length === 0) {
                fetch(`${API}/admin/verb-explorer/categories`, { headers: { Authorization: `Bearer ${adminToken}` } })
                  .then(r => r.json()).then(d => {
                    setVerbExplorerCategories(d.categories || []);
                  }).catch(() => {});
                fetch(`${API}/admin/verb-explorer/all-verbs`, { headers: { Authorization: `Bearer ${adminToken}` } })
                  .then(r => r.json()).then(d => setVerbExplorerAllVerbs(d.verbs || [])).catch(() => {});
              }
              if (tab.key === 'access' && verbExplorerVerbAccess.length === 0) {
                fetch(`${API}/admin/verb-explorer/verb-access`, { headers: { Authorization: `Bearer ${adminToken}` } })
                  .then(r => r.json()).then(d => setVerbExplorerVerbAccess(d.verbs || [])).catch(() => {});
              }
              if (tab.key === 'images') {
                // Always reload fresh data for images tab
                fetch(`${API}/admin/verb-explorer/verb-access`, { headers: { Authorization: `Bearer ${adminToken}` } })
                  .then(r => r.json())
                  .then(d => {
                    const verbs = (d.verbs || []).map(v => ({
                      ...v,
                      categoryName: v.category?.name || 'Uncategorized'
                    }));
                    setVerbImageData(verbs);
                  }).catch(() => {});
              }
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              verbExplorerSubTab === tab.key
                ? (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                : (isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* SETTINGS SUB-TAB */}
      {verbExplorerSubTab === 'settings' && (
        <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Verb Explorer Settings</h3>
          <div className="space-y-4">
            {/* Visibility toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Visible to users</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Show Verb Explorer in the sidebar</p>
              </div>
              <button onClick={() => setVerbExplorerSettings(p => ({ ...p, is_visible: !p.is_visible }))}
                className={`w-12 h-6 rounded-full transition-colors ${verbExplorerSettings.is_visible ? 'bg-green-500' : (isDarkMode ? 'bg-gray-600' : 'bg-gray-300')}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${verbExplorerSettings.is_visible ? 'translate-x-6' : 'translate-x-0.5'}`}/>
              </button>
            </div>
            {/* Pro-only toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Pro-only</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Entire feature requires Pro (overrides per-verb access)</p>
              </div>
              <button onClick={() => setVerbExplorerSettings(p => ({ ...p, is_pro_only: !p.is_pro_only }))}
                className={`w-12 h-6 rounded-full transition-colors ${verbExplorerSettings.is_pro_only ? 'bg-purple-500' : (isDarkMode ? 'bg-gray-600' : 'bg-gray-300')}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${verbExplorerSettings.is_pro_only ? 'translate-x-6' : 'translate-x-0.5'}`}/>
              </button>
            </div>
            {/* Beta badge toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Beta badge</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Show beta badge on the page</p>
              </div>
              <button onClick={() => setVerbExplorerSettings(p => ({ ...p, beta_badge_visible: !p.beta_badge_visible }))}
                className={`w-12 h-6 rounded-full transition-colors ${verbExplorerSettings.beta_badge_visible ? 'bg-amber-500' : (isDarkMode ? 'bg-gray-600' : 'bg-gray-300')}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${verbExplorerSettings.beta_badge_visible ? 'translate-x-6' : 'translate-x-0.5'}`}/>
              </button>
            </div>
            {verbExplorerSettings.beta_badge_visible && (
              <div className="space-y-2">
                <input type="text" value={verbExplorerSettings.beta_badge_text || ''}
                  onChange={e => setVerbExplorerSettings(p => ({ ...p, beta_badge_text: e.target.value }))}
                  placeholder="Badge label (e.g. Beta)"
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <input type="text" value={verbExplorerSettings.beta_badge_description || ''}
                  onChange={e => setVerbExplorerSettings(p => ({ ...p, beta_badge_description: e.target.value }))}
                  placeholder="Badge description (e.g. This feature is in beta)"
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            )}
            {/* Rate limit */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Rate limit (per hour)</p>
                <button onClick={() => setVerbExplorerSettings(p => ({ ...p, rate_limit_enabled: !p.rate_limit_enabled }))}
                  className={`w-12 h-6 rounded-full transition-colors ${verbExplorerSettings.rate_limit_enabled ? 'bg-blue-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${verbExplorerSettings.rate_limit_enabled ? 'translate-x-6' : 'translate-x-0.5'}`}/>
                </button>
              </div>
              {verbExplorerSettings.rate_limit_enabled && (
                <input type="number" min="1" max="200" value={verbExplorerSettings.rate_limit_per_hour || 30}
                  onChange={e => setVerbExplorerSettings(p => ({ ...p, rate_limit_per_hour: parseInt(e.target.value) || 30 }))}
                  className={`w-32 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              )}
              {!verbExplorerSettings.rate_limit_enabled && (
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rate limiting is disabled — unlimited requests allowed</p>
              )}
            </div>
            {/* "All" category icon */}
            <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>"All" Category Icon</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Custom icon for the "All" button (replaces default 📚)</p>
              </div>
              <div className="flex items-center gap-2">
                {verbExplorerSettings.all_icon_url ? (
                  <>
                    <img src={`${API.replace('/api', '')}${verbExplorerSettings.all_icon_url}`} alt="" className="w-8 h-8 object-contain rounded" />
                    <button onClick={() => setVerbExplorerSettings(p => ({ ...p, all_icon_url: '' }))}
                      className="text-red-400 text-xs hover:text-red-300">✕</button>
                  </>
                ) : <span className="text-2xl">📚</span>}
                <button onClick={() => { setIconPickerTarget('all'); setIconPickerOpen(true); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>
                  Pick Icon
                </button>
              </div>
            </div>

            {/* Page Title Icon */}
            <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Page Title Icon</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Icon next to the page title (replaces default 📖)</p>
              </div>
              <div className="flex items-center gap-2">
                {verbExplorerSettings.page_icon_url ? (
                  <>
                    <img src={`${API.replace('/api', '')}${verbExplorerSettings.page_icon_url}`} alt="" className="w-8 h-8 object-contain rounded" />
                    <button onClick={() => setVerbExplorerSettings(p => ({ ...p, page_icon_url: '' }))}
                      className="text-red-400 text-xs hover:text-red-300">✕</button>
                  </>
                ) : <span className="text-2xl">📖</span>}
                <button onClick={() => { setIconPickerTarget('page'); setIconPickerOpen(true); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>
                  Pick Icon
                </button>
              </div>
            </div>

            {/* Page Title & Subtitle */}
            <div className="space-y-2 pt-4" style={{ borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Page Title</label>
                <input type="text" value={verbExplorerSettings.page_title || ''}
                  onChange={e => setVerbExplorerSettings(p => ({ ...p, page_title: e.target.value }))}
                  placeholder="Darija Verb Explorer"
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Page Subtitle</label>
                <input type="text" value={verbExplorerSettings.page_subtitle || ''}
                  onChange={e => setVerbExplorerSettings(p => ({ ...p, page_subtitle: e.target.value }))}
                  placeholder="Browse & learn Moroccan verb conjugations"
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            </div>

            {/* Save button */}
            <button onClick={async () => {
              setVeSaving(true);
              try {
                const res = await fetch(`${API}/admin/verb-explorer/settings`, {
                  method: 'PUT',
                  headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify(verbExplorerSettings)
                });
                if (res.ok) showNotification('Settings saved', 'success');
                else showNotification('Failed to save', 'error');
              } catch { showNotification('Failed to save', 'error'); }
              setVeSaving(false);
            }}
              disabled={veSaving}
              className={`px-6 py-2 rounded-lg text-sm font-medium text-white ${veSaving ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'}`}
            >
              {veSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {/* CATEGORIES SUB-TAB */}
      {verbExplorerSubTab === 'categories' && (
        <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Categories ({verbExplorerCategories.length})</h3>
            <button onClick={() => setEditingVECategory({ isNew: true, ...newVECategory })}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-green-500 text-white hover:bg-green-600">
              + Add Category
            </button>
          </div>

          {/* Add/Edit category form */}
          {editingVECategory && (
            <div className={`p-4 rounded-lg border mb-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex gap-3 mb-3">
                <input type="text" placeholder="Category name" value={editingVECategory.name || ''}
                  onChange={e => setEditingVECategory(p => ({ ...p, name: e.target.value }))}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
                <input type="text" placeholder="Icon" value={editingVECategory.icon || ''}
                  onChange={e => setEditingVECategory(p => ({ ...p, icon: e.target.value }))}
                  className={`w-16 px-3 py-2 rounded-lg border text-sm text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
                {editingVECategory.icon_url && (
                  <img src={`${API.replace('/api', '')}${editingVECategory.icon_url}`} alt="" className="w-8 h-8 object-contain rounded" />
                )}
                <button type="button" onClick={() => { setIconPickerTarget('form'); setIconPickerOpen(true); }}
                  className={`px-2 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>
                  Pick Icon
                </button>
                {editingVECategory.icon_url && (
                  <button type="button" onClick={() => setEditingVECategory(p => ({ ...p, icon_url: '' }))}
                    className="text-red-400 text-xs hover:text-red-300">✕</button>
                )}
                <input type="number" placeholder="Order" value={editingVECategory.order || 0}
                  onChange={e => setEditingVECategory(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                  className={`w-20 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
              <div className="flex gap-2">
                <button onClick={async () => {
                  if (!editingVECategory.name) return;
                  setVeSaving(true);
                  try {
                    const isNew = editingVECategory.isNew;
                    const url = isNew ? `${API}/admin/verb-explorer/categories` : `${API}/admin/verb-explorer/categories/${editingVECategory.id}`;
                    const method = isNew ? 'POST' : 'PUT';
                    const res = await fetch(url, {
                      method,
                      headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ name: editingVECategory.name, icon: editingVECategory.icon, icon_url: editingVECategory.icon_url || '', order: editingVECategory.order })
                    });
                    if (res.ok) {
                      showNotification(isNew ? 'Category created' : 'Category updated', 'success');
                      const catRes = await fetch(`${API}/admin/verb-explorer/categories`, { headers: { Authorization: `Bearer ${adminToken}` } });
                      if (catRes.ok) { const d = await catRes.json(); setVerbExplorerCategories(d.categories || []); }
                      setEditingVECategory(null);
                    } else showNotification('Failed to save', 'error');
                  } catch { showNotification('Error saving', 'error'); }
                  setVeSaving(false);
                }} disabled={veSaving}
                  className="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-blue-500 hover:bg-blue-600">
                  {veSaving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={() => setEditingVECategory(null)}
                  className={`px-4 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Category list */}
          <div className="space-y-2">
            {verbExplorerCategories.map(cat => (
              <div key={cat.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {cat.icon_url
                      ? <img src={`${API.replace('/api', '')}${cat.icon_url}`} alt="" className="w-6 h-6 object-contain" />
                      : <span className="text-lg">{cat.icon}</span>}
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{cat.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                      {cat.verb_count} verbs
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>order: {cat.order}</span>
                    {!cat.is_active && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600">Hidden</span>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => {
                      setVeAssigningCategory(veAssigningCategory === cat.id ? null : cat.id);
                    }}
                      className={`px-2 py-1 rounded text-xs font-medium ${veAssigningCategory === cat.id ? 'bg-blue-500 text-white' : (isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')}`}>
                      {veAssigningCategory === cat.id ? 'Close' : 'Assign Verbs'}
                    </button>
                    <button onClick={() => { setIconPickerTarget(cat.id); setIconPickerOpen(true); }}
                      className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-purple-700 text-purple-200 hover:bg-purple-600' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}>
                      Icon
                    </button>
                    <button onClick={() => setEditingVECategory({ ...cat })}
                      className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      Edit
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm(`Delete category "${cat.name}"? Verbs will become uncategorized.`)) return;
                      const res = await fetch(`${API}/admin/verb-explorer/categories/${cat.id}`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${adminToken}` }
                      });
                      if (res.ok) {
                        showNotification('Category deleted', 'success');
                        setVerbExplorerCategories(prev => prev.filter(c => c.id !== cat.id));
                      }
                    }}
                      className="px-2 py-1 rounded text-xs bg-red-500/10 text-red-500 hover:bg-red-500/20">
                      Delete
                    </button>
                  </div>
                </div>

                {/* Verb assignment panel */}
                {veAssigningCategory === cat.id && (
                  <div className={`mt-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-50 border-gray-200'}`}>
                    <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Select verbs for "{cat.name}":
                    </p>
                    <div className="flex flex-wrap gap-1.5 max-h-60 overflow-y-auto">
                      {verbExplorerAllVerbs.map(verb => {
                        const isInThis = (cat.verbs || []).some(v => (typeof v === 'object' ? v.name : v) === verb);
                        const otherCat = verbExplorerCategories.find(c => c.id !== cat.id && (c.verbs || []).some(v => (typeof v === 'object' ? v.name : v) === verb));
                        return (
                          <button key={verb}
                            onClick={() => {
                              setVerbExplorerCategories(prev => prev.map(c => {
                                if (c.id !== cat.id) return c;
                                const currentVerbs = c.verbs || [];
                                const existing = currentVerbs.find(v => (typeof v === 'object' ? v.name : v) === verb);
                                if (existing) {
                                  return { ...c, verbs: currentVerbs.filter(v => (typeof v === 'object' ? v.name : v) !== verb), verb_count: currentVerbs.length - 1 };
                                } else {
                                  const newVerbs = [...currentVerbs, { name: verb, access: 'pro' }];
                                  return { ...c, verbs: newVerbs, verb_count: newVerbs.length };
                                }
                              }));
                            }}
                            disabled={!!otherCat}
                            className={`px-2 py-1 rounded text-xs capitalize transition-all ${
                              isInThis ? 'bg-blue-500 text-white' :
                              otherCat ? (isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed') :
                              (isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border')
                            }`}
                            title={otherCat ? `In "${otherCat.name}"` : ''}
                          >
                            {verb} {otherCat ? `(${otherCat.name})` : ''}
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={async () => {
                      setVeSaving(true);
                      const catData = verbExplorerCategories.find(c => c.id === cat.id);
                      const res = await fetch(`${API}/admin/verb-explorer/categories/${cat.id}/verbs`, {
                        method: 'PUT',
                        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ verbs: catData?.verbs || [] })
                      });
                      if (res.ok) showNotification('Verbs assigned', 'success');
                      else showNotification('Failed to save', 'error');
                      setVeSaving(false);
                    }}
                      disabled={veSaving}
                      className="mt-2 px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-blue-500 hover:bg-blue-600">
                      {veSaving ? 'Saving...' : 'Save Assignments'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* VERB ACCESS SUB-TAB */}
      {verbExplorerSubTab === 'access' && (
        <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Verb Access Control ({verbExplorerVerbAccess.length} verbs)
            </h3>
            <div className="flex gap-2">
              <button onClick={() => setVerbExplorerVerbAccess(prev => prev.map(v => ({ ...v, access: 'free' })))}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500/10 text-green-600 hover:bg-green-500/20">
                Set All Free
              </button>
              <button onClick={() => setVerbExplorerVerbAccess(prev => prev.map(v => ({ ...v, access: 'pro' })))}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-500/10 text-purple-600 hover:bg-purple-500/20">
                Set All Pro
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 max-h-96 overflow-y-auto">
            {verbExplorerVerbAccess.map((verb, idx) => (
              <button key={verb.name}
                onClick={() => {
                  setVerbExplorerVerbAccess(prev => prev.map((v, i) => i === idx ? { ...v, access: v.access === 'free' ? 'pro' : 'free' } : v));
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all border ${
                  verb.access === 'free'
                    ? (isDarkMode ? 'bg-green-900/30 border-green-700 text-green-400' : 'bg-green-50 border-green-300 text-green-700')
                    : (isDarkMode ? 'bg-purple-900/30 border-purple-700 text-purple-400' : 'bg-purple-50 border-purple-300 text-purple-700')
                }`}
              >
                {verb.access === 'free' ? '✓' : '🔒'} {verb.name}
                {verb.category && <span className={`ml-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>({verb.category.name})</span>}
              </button>
            ))}
          </div>
          <button onClick={async () => {
            setVeSaving(true);
            try {
              const res = await fetch(`${API}/admin/verb-explorer/verb-access`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ verbs: verbExplorerVerbAccess.map(v => ({ name: v.name, access: v.access })) })
              });
              if (res.ok) showNotification('Verb access updated', 'success');
              else showNotification('Failed to save', 'error');
            } catch { showNotification('Error saving', 'error'); }
            setVeSaving(false);
          }}
            disabled={veSaving}
            className={`mt-4 px-6 py-2 rounded-lg text-sm font-medium text-white ${veSaving ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'}`}
          >
            {veSaving ? 'Saving...' : 'Save Access Changes'}
          </button>
        </div>
      )}

      {/* VERB IMAGES SUB-TAB */}
      {verbExplorerSubTab === 'images' && (
        <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Verb Images ({verbImageData.length} verbs)
            </h3>
            <div className="flex gap-2 items-center">
              <button onClick={async () => {
                setVeSaving(true);
                try {
                  const res = await fetch(`${API}/admin/verb-explorer/verb-bulk-visibility`, {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ is_visible: true })
                  });
                  if (res.ok) {
                    setVerbImageData(prev => prev.map(v => ({ ...v, is_visible: true })));
                    showNotification('All verbs shown', 'success');
                  }
                } catch { showNotification('Error', 'error'); }
                setVeSaving(false);
              }} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500/10 text-green-600 hover:bg-green-500/20">
                Show All
              </button>
              <button onClick={async () => {
                setVeSaving(true);
                try {
                  const res = await fetch(`${API}/admin/verb-explorer/verb-bulk-visibility`, {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ is_visible: false })
                  });
                  if (res.ok) {
                    setVerbImageData(prev => prev.map(v => ({ ...v, is_visible: false })));
                    showNotification('All verbs hidden', 'success');
                  }
                } catch { showNotification('Error', 'error'); }
                setVeSaving(false);
              }} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-600 hover:bg-red-500/20">
                Hide All
              </button>
            </div>
          </div>

          {/* Search & Filter */}
          <div className="flex gap-3 mb-4">
            <input type="text" placeholder="Search verbs..." value={verbImageSearch}
              onChange={e => setVerbImageSearch(e.target.value)}
              className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
            />
            <select value={verbImageFilter} onChange={e => setVerbImageFilter(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
              <option value="all">All verbs</option>
              <option value="with-images">With images</option>
              <option value="no-images">No images</option>
            </select>
          </div>

          {/* Verb list */}
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {(() => {
              let filtered = verbImageData;
              if (verbImageSearch.trim()) {
                const q = verbImageSearch.toLowerCase();
                filtered = filtered.filter(v => v.name.toLowerCase().includes(q));
              }
              if (verbImageFilter === 'with-images') {
                filtered = filtered.filter(v => v.he_image || v.she_image);
              } else if (verbImageFilter === 'no-images') {
                filtered = filtered.filter(v => !v.he_image && !v.she_image);
              }
              // Group by category
              const grouped = {};
              filtered.forEach(v => {
                const cat = v.categoryName || 'Uncategorized';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(v);
              });

              if (Object.keys(grouped).length === 0) {
                return <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No verbs found</p>;
              }

              return Object.entries(grouped).map(([catName, verbs]) => (
                <div key={catName} className="mb-4">
                  <h4 className={`text-sm font-semibold mb-2 px-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {catName} ({verbs.length})
                  </h4>
                  <div className="space-y-1.5">
                    {verbs.map(verb => (
                      <div key={verb.name}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${
                          isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'
                        } ${!verb.is_visible ? 'opacity-50' : ''}`}>
                        {/* Verb name + badges */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{verb.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              verb.access === 'free'
                                ? (isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700')
                                : (isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-700')
                            }`}>
                              {verb.access === 'free' ? 'Free' : 'Pro'}
                            </span>
                            {!verb.is_visible && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600">Hidden</span>
                            )}
                          </div>
                        </div>

                        {/* He image */}
                        <div className="flex flex-col items-center gap-1">
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>He</span>
                          {verb.he_image ? (
                            <div className="relative group">
                              <img src={`${API.replace('/api', '')}${verb.he_image}`} alt="" className="w-10 h-10 rounded object-cover" />
                              <button onClick={() => {
                                // Clear he_image
                                fetch(`${API}/admin/verb-explorer/verb-image`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ verb_name: verb.name, category_id: verb.category?.id || '', he_image: '', she_image: verb.she_image || '' })
                                }).then(r => {
                                  if (r.ok) {
                                    setVerbImageData(prev => prev.map(v => v.name === verb.name ? { ...v, he_image: '' } : v));
                                    showNotification('Image removed', 'success');
                                  }
                                }).catch(() => {});
                              }}
                                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                ×
                              </button>
                            </div>
                          ) : (
                            <button onClick={async () => {
                              setImagePickerTarget({ verbName: verb.name, categoryId: verb.category?.id || '', field: 'he_image', currentShe: verb.she_image || '' });
                              setImagePickerSearch('');
                              setImagePickerSelectedFolder(null);
                              setImagePickerAssets([]);
                              setImagePickerLoading(true);
                              try {
                                const r = await fetch(`${API}/admin/image-folders`, { headers: { Authorization: `Bearer ${adminToken}` } });
                                if (r.ok) { const d = await r.json(); setImagePickerFolders(d.folders || []); }
                              } catch {}
                              setImagePickerLoading(false);
                              setImagePickerOpen(true);
                            }}
                              className={`w-10 h-10 rounded border-2 border-dashed flex items-center justify-center text-lg ${isDarkMode ? 'border-gray-500 text-gray-500 hover:border-gray-400' : 'border-gray-300 text-gray-400 hover:border-gray-400'}`}>
                              +
                            </button>
                          )}
                        </div>

                        {/* She image */}
                        <div className="flex flex-col items-center gap-1">
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>She</span>
                          {verb.she_image ? (
                            <div className="relative group">
                              <img src={`${API.replace('/api', '')}${verb.she_image}`} alt="" className="w-10 h-10 rounded object-cover" />
                              <button onClick={() => {
                                fetch(`${API}/admin/verb-explorer/verb-image`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ verb_name: verb.name, category_id: verb.category?.id || '', he_image: verb.he_image || '', she_image: '' })
                                }).then(r => {
                                  if (r.ok) {
                                    setVerbImageData(prev => prev.map(v => v.name === verb.name ? { ...v, she_image: '' } : v));
                                    showNotification('Image removed', 'success');
                                  }
                                }).catch(() => {});
                              }}
                                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                ×
                              </button>
                            </div>
                          ) : (
                            <button onClick={async () => {
                              setImagePickerTarget({ verbName: verb.name, categoryId: verb.category?.id || '', field: 'she_image', currentHe: verb.he_image || '' });
                              setImagePickerSearch('');
                              setImagePickerSelectedFolder(null);
                              setImagePickerAssets([]);
                              setImagePickerLoading(true);
                              try {
                                const r = await fetch(`${API}/admin/image-folders`, { headers: { Authorization: `Bearer ${adminToken}` } });
                                if (r.ok) { const d = await r.json(); setImagePickerFolders(d.folders || []); }
                              } catch {}
                              setImagePickerLoading(false);
                              setImagePickerOpen(true);
                            }}
                              className={`w-10 h-10 rounded border-2 border-dashed flex items-center justify-center text-lg ${isDarkMode ? 'border-gray-500 text-gray-500 hover:border-gray-400' : 'border-gray-300 text-gray-400 hover:border-gray-400'}`}>
                              +
                            </button>
                          )}
                        </div>

                        {/* Visibility toggle */}
                        <button onClick={async () => {
                          const newVisible = !verb.is_visible;
                          try {
                            const res = await fetch(`${API}/admin/verb-explorer/verb-visibility`, {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ verb_name: verb.name, category_id: verb.category?.id || '', is_visible: newVisible })
                            });
                            if (res.ok) {
                              setVerbImageData(prev => prev.map(v => v.name === verb.name ? { ...v, is_visible: newVisible } : v));
                              showNotification(newVisible ? 'Verb shown' : 'Verb hidden', 'success');
                            }
                          } catch {}
                        }}
                          className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 ${verb.is_visible ? 'bg-green-500' : (isDarkMode ? 'bg-gray-600' : 'bg-gray-300')}`}>
                          <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${verb.is_visible ? 'translate-x-4' : 'translate-x-0.5'}`}/>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>

      {/* Image Picker Modal (for verb he/she images) — shows folders then images */}
      {imagePickerOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setImagePickerOpen(false)}>
          <div className={`w-full max-w-3xl max-h-[85vh] rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {imagePickerSelectedFolder && (
                  <button onClick={() => { setImagePickerSelectedFolder(null); setImagePickerAssets([]); setImagePickerSearch(''); }}
                    className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                    ←
                  </button>
                )}
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {imagePickerSelectedFolder
                    ? `${imagePickerSelectedFolder.name} — Pick ${imagePickerTarget?.field === 'he_image' ? 'He' : 'She'} image for "${imagePickerTarget?.verbName}"`
                    : `Select Folder — ${imagePickerTarget?.field === 'he_image' ? 'He' : 'She'} image for "${imagePickerTarget?.verbName}"`}
                </h3>
              </div>
              <button onClick={() => setImagePickerOpen(false)} className={`p-2 rounded-lg text-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>✕</button>
            </div>

            {imagePickerLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : !imagePickerSelectedFolder ? (
              /* Folder List */
              imagePickerFolders.length === 0 ? (
                <p className={`text-base text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No folders found. Create a folder in Image Manager first.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 overflow-y-auto max-h-[60vh] p-2">
                  {imagePickerFolders.map(folder => (
                    <button key={folder.id} onClick={async () => {
                      setImagePickerLoading(true);
                      setImagePickerSearch('');
                      try {
                        const r = await fetch(`${API}/admin/image-assets/${folder.id}`, { headers: { Authorization: `Bearer ${adminToken}` } });
                        if (r.ok) { const d = await r.json(); setImagePickerAssets(d.assets || []); }
                      } catch {}
                      setImagePickerSelectedFolder(folder);
                      setImagePickerLoading(false);
                    }}
                      className={`p-4 rounded-xl border-2 transition-all hover:scale-105 text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 hover:border-purple-500' : 'bg-gray-50 border-gray-200 hover:border-purple-400'}`}>
                      <span className="text-3xl block mb-2">{folder.icon || '📁'}</span>
                      <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{folder.name}</p>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{folder.asset_count || 0} images</p>
                    </button>
                  ))}
                </div>
              )
            ) : (
              /* Images in selected folder */
              <>
                <input type="text" placeholder="Search images in this folder..." value={imagePickerSearch} onChange={e => setImagePickerSearch(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl mb-4 text-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-purple-500`} />
                {imagePickerAssets.length === 0 ? (
                  <p className={`text-base text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No images in this folder. Upload images in Image Manager first.</p>
                ) : (() => {
                  const filtered = imagePickerSearch.trim()
                    ? imagePickerAssets.filter(a => (a.name || '').toLowerCase().includes(imagePickerSearch.toLowerCase()) || (a.tags || []).some(t => t.toLowerCase().includes(imagePickerSearch.toLowerCase())))
                    : imagePickerAssets;
                  return filtered.length === 0
                    ? <p className={`text-base text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No images match "{imagePickerSearch}"</p>
                    : (
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 overflow-y-auto max-h-[55vh] p-2">
                      {filtered.map(asset => (
                        <button key={asset.id} onClick={async () => {
                          const target = imagePickerTarget;
                          const body = {
                            verb_name: target.verbName,
                            category_id: target.categoryId,
                            he_image: target.field === 'he_image' ? asset.url : (target.currentHe || ''),
                            she_image: target.field === 'she_image' ? asset.url : (target.currentShe || '')
                          };
                          try {
                            const res = await fetch(`${API}/admin/verb-explorer/verb-image`, {
                              method: 'PUT',
                              headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify(body)
                            });
                            if (res.ok) {
                              setVerbImageData(prev => prev.map(v => v.name === target.verbName
                                ? { ...v, [target.field]: asset.url }
                                : v
                              ));
                              showNotification('Image assigned!', 'success');
                            } else {
                              showNotification('Failed to assign image', 'error');
                            }
                          } catch { showNotification('Error assigning image', 'error'); }
                          setImagePickerOpen(false);
                        }} className={`p-2 rounded-xl border-2 transition-all hover:scale-105 ${isDarkMode ? 'bg-gray-700 border-gray-600 hover:border-purple-500' : 'bg-gray-50 border-gray-200 hover:border-purple-400'}`} title={asset.name || ''}>
                          <img src={`${API.replace('/api', '')}${asset.url}`} alt={asset.name || ''} className="w-20 h-20 object-contain mx-auto rounded" />
                          <p className={`text-xs truncate mt-1 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{asset.name || ''}</p>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}

      {/* Icon Picker Modal */}
      {iconPickerOpen && (
        <IconPicker
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          onClose={() => setIconPickerOpen(false)}
          onPick={(url) => handleIconSelect(url)}
        />
      )}
    </>
  );
};

export default VerbExplorerAdmin;
